// Collection filters apply as soon as a choice changes (a tick, a price field, the sort): the section is rendered
// again through the Section Rendering API and swapped in place, the address follows the choice (back and forward
// step through earlier choices) and the open filter panel stays open. On phones and tablets the panel's button
// closes the sheet and shows the result count; from 990px it is hidden (joy-collection.css). If a request fails,
// the browser loads the filtered page normally.
(() => {
  const wide = window.matchMedia('(min-width: 990px)');
  document.documentElement.classList.add('instant-filters');

  // Phones show two lines of the collection description (joy-collection.css); "Devamı" appears only when more is hidden.
  const markLongDescription = (scope = document) => {
    const text = scope.querySelector('[data-collection-description]');
    const more = scope.querySelector('[data-collection-more]');
    if (text && more) more.hidden = text.classList.contains('is-open') || text.scrollHeight <= text.clientHeight + 2;
  };
  markLongDescription();
  window.addEventListener('resize', () => markLongDescription());
  document.addEventListener('click', (event) => {
    const more = event.target.closest('[data-collection-more]');
    if (!more) return;
    more.previousElementSibling?.classList.add('is-open');
    more.hidden = true;
  });

  const sectionOf = (element) => element?.closest('.shopify-section');
  const currentSection = () => sectionOf(document.querySelector('.collection-page'));

  const urlFor = (form) => {
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(form)) {
      if (value !== '') params.append(key, value); // an empty price field would otherwise filter to nothing
    }
    const query = params.toString();
    return `${new URL(form.action, location.href).pathname}${query ? `?${query}` : ''}`;
  };

  // The typed fields (price) by name, so what the visitor types while a render is on its way survives the swap.
  const typedValues = (root) => new Map([...root.querySelectorAll('.collection-filters input[type=number], .collection-filters input[type=text]')]
    .filter((input) => input.name)
    .map((input) => [input.name, input.value]));

  let controller;
  let latest = 0;
  // The address the shown results belong to: a back or forward step that only changes the #hash renders nothing.
  let shown = location.pathname + location.search;
  const render = async (url, { push = true, closePanel = false } = {}) => {
    const section = currentSection();
    if (!section) return;
    controller?.abort();
    controller = new AbortController();
    const ticket = ++latest;
    const panel = section.querySelector('[data-filter-panel]');
    const wasOpen = panel?.open && !closePanel;
    const sent = typedValues(section);
    const sheetScroll = section.querySelector('.filters-body')?.scrollTop || 0;
    section.setAttribute('aria-busy', 'true');
    try {
      const target = new URL(url, location.href);
      target.searchParams.set('section_id', section.id.replace('shopify-section-', ''));
      const response = await fetch(target, { signal: controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const fresh = new DOMParser().parseFromString(await response.text(), 'text/html').getElementById(section.id);
      if (!fresh) throw new Error('section missing');
      // Read focus, caret and typed values now, not when the request left: the visitor may have moved on.
      const active = section.contains(document.activeElement) ? document.activeElement : null;
      // The price fields have no id: find them again by name.
      const focusedSelector = active?.id ? `#${CSS.escape(active.id)}` : active?.name ? `.collection-filters [name="${CSS.escape(active.name)}"]` : '';
      const caret = active && typeof active.selectionStart === 'number' ? [active.selectionStart, active.selectionEnd] : null;
      const typed = typedValues(section);
      section.innerHTML = fresh.innerHTML;
      // The new cards arrive with empty hearts: mark the saved ones again.
      window.Joy?.syncWishlist?.(section);
      markLongDescription(section);
      typed.forEach((value, name) => {
        if (value === sent.get(name)) return; // unchanged since the request: the fresh value is right
        section.querySelectorAll(`.collection-filters input[name="${CSS.escape(name)}"]`).forEach((input) => { input.value = value; });
      });
      const newPanel = section.querySelector('[data-filter-panel]');
      if (newPanel) {
        newPanel.open = wide.matches || wasOpen;
        const body = newPanel.querySelector('.filters-body');
        if (body && wasOpen) body.scrollTop = sheetScroll;
      }
      const refocus = focusedSelector ? section.querySelector(focusedSelector) : null;
      if (refocus) {
        refocus.focus({ preventScroll: true });
        if (caret) try { refocus.setSelectionRange(...caret); } catch (e) { /* number fields have no caret */ }
      }
      if (push) history.pushState({ joyFilters: true }, '', url);
      const done = new URL(url, location.href);
      shown = done.pathname + done.search;
    } catch (error) {
      if (error.name !== 'AbortError') location.assign(url);
    } finally {
      if (ticket === latest) section.removeAttribute('aria-busy');
    }
  };

  // Ticks, price fields (on leaving the field) and the panel's sort select. A field that a render has just
  // replaced still reports its change; it is out of the page by then, so it is ignored.
  document.addEventListener('change', (event) => {
    if (!event.target.isConnected) return;
    const form = event.target.closest?.('.collection-filters');
    if (!form || event.target.matches('[data-quick-sort]')) return; // the bar's "Sırala" submits (joy.js)
    render(urlFor(form));
  });

  // Enter in a price field, the bar's "Sırala" and the panel button: render in place; the button closes the
  // phone sheet so the results show.
  document.addEventListener('submit', (event) => {
    const form = event.target.closest?.('.collection-filters');
    if (!form) return;
    event.preventDefault();
    render(urlFor(form), { closePanel: !wide.matches && event.submitter?.type === 'submit' });
  });

  // Removing an active filter and "Filtreleri Temizle" also stay on the page.
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('.active-filters a, .filter-reset');
    if (!link || !currentSection()?.contains(link)) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    render(link.getAttribute('href'));
  });

  // Toplam Karata Göre (snippets/joy-carat-bands.liquid): one band shown at a time.
  document.addEventListener('click', (event) => {
    const chip = event.target.closest?.('[data-band-chip]');
    if (!chip) return;
    const box = chip.closest('[data-carat-bands]');
    box.querySelectorAll('[data-band-chip]').forEach((other) => other.setAttribute('aria-pressed', String(other === chip)));
    box.querySelectorAll('[data-band]').forEach((band) => { band.hidden = band.dataset.band !== chip.dataset.bandChip; });
  });

  // Moved from joy.js (2026-10-20): filter panel open state and the sheet position, collection pages only.
  // The filter panel is closed in the HTML (no flash on phones); desktop shows it open.
  // Phones and tablets: the open filter panel is a sheet under the bar; its top follows the bar's bottom edge.
  const placeFilterSheet = () => {
    if (wide.matches) return;
    const heading = document.querySelector('[data-filter-panel][open]>summary');
    if (heading) document.documentElement.style.setProperty('--filter-sheet-top', `${Math.max(0, Math.round(heading.parentElement.parentElement.getBoundingClientRect().bottom))}px`);
  };
  document.addEventListener('toggle', (event) => {
    if (event.target.matches?.('[data-filter-panel]') && event.target.open) placeFilterSheet();
  }, true);
  window.addEventListener('scroll', () => {
    if (!wide.matches && document.querySelector('[data-filter-panel][open]')) placeFilterSheet();
  }, { passive: true });
  window.addEventListener('resize', placeFilterSheet);
  const prepareFilters = (scope = document) => {
    if (!wide.matches) return;
    scope.querySelectorAll('[data-filter-panel]').forEach((panel) => { panel.open = true; });
  };
  prepareFilters();
  document.addEventListener('shopify:section:load', (event) => prepareFilters(event.target));
  // Rotating an iPad or resizing Split View across 990px: the desktop layout cannot tap the heading
  // (joy.css), so open the panel there; back in the tablet layout, restore what the customer left
  // there (closed unless they opened it).
  const tabletFilters = new WeakMap();
  wide.addEventListener('change', () => {
    document.querySelectorAll('[data-filter-panel]').forEach((panel) => {
      if (wide.matches) {
        tabletFilters.set(panel, panel.open);
        panel.open = true;
      } else {
        panel.open = tabletFilters.get(panel) === true;
      }
    });
  });
  // Collection "Sırala" in the sticky filter bar: copies the choice into the panel's select and sorts at once.
  document.addEventListener('change', (event) => {
    const quick = event.target.closest?.('[data-quick-sort]');
    if (!quick) return;
    const form = quick.form;
    const select = form?.querySelector('select[name="sort_by"]');
    if (!select) return;
    select.value = quick.value;
    form.requestSubmit ? form.requestSubmit() : form.submit();
  });
  window.addEventListener('popstate', () => {
    if (!currentSection() || location.pathname + location.search === shown) return;
    render(location.href, { push: false });
  });
})();
